import { Logistic } from 'gql/autogen/Logistic';
import { DetailsActivityResult } from 'gql/autogen/DetailsActivityResult';
import { ItineraryReference } from 'gql/autogen/ItineraryReference';
import { LocationRef } from 'gql/autogen/LocationRef';
import { getElementAtIndex } from 'core-framework/util/CommonUtils';
import { mapLoyaltyOfferByPricing } from 'gql/service/graph/activityService';
import { getBookingFormUrl } from 'core/utils/activityUrlGenerator';
import { OverlayType } from 'store/slice/overlay';
import { AvailabilitySchedule } from 'gql/autogen/AvailabilitySchedule';
import { format, addDays, parseISO } from 'date-fns';
import { PriceFormatter } from '@agoda/react-handyman';
import { ActivityAvailabilityStatus } from 'gql/autogen/ActivityAvailabilityStatus';
import { ActivityLocation } from 'gql/autogen/ActivityLocation';
import { EnrichedOfferOption } from 'gql/autogen/EnrichedOfferOption';
import { Detail } from 'gql/autogen/Detail';
import { OfferGroupDetail } from 'gql/autogen/OfferGroupDetail';
import { Pricing } from 'gql/autogen/Pricing';
import { DisplayPromocodeItem } from 'gql/autogen/DisplayPromocodeItem';
import { Experiments } from 'core/type/BootstrapConfig.types';
import { isB } from 'core/window/bootstrapConfig';
import { ExperimentId } from 'core/type/ExperimentId';

export const mapAdditionalInfo = (result?: DetailsActivityResult): string[] => {
    if (!result?.activity?.content?.detail?.additionalDetails) {
        return [];
    }

    return result.activity.content.detail.additionalDetails.map((detail) => (detail.description ? detail.description.join(' ') : ''));
};

const isValidLocation = (location: LocationRef, isContentReadEnabled: boolean): boolean => {
    return isContentReadEnabled ? !!location.description || (location.locationRefId || 0) >= 0 : !!location.description || (location.locationRefId || 0) > 0;
};

export const isDepartureAndReturnAvailable = (logistic: Logistic, isContentReadEnabled: boolean): boolean => {
    const startEnd = logistic && logistic.startEnd;
    if (!startEnd) return false;
    const validStartLocations = (startEnd.startLocation || []).filter((location) => isValidLocation(location, isContentReadEnabled));
    const validEndLocation = (startEnd.endLocation || []).filter((location) => isValidLocation(location, isContentReadEnabled));
    return validStartLocations.length > 0 || validEndLocation.length > 0;
};

export const isGeoLocationAvailable = (activityLocation: ActivityLocation | undefined): boolean => {
    const { geo } = activityLocation || {};
    return !!geo && typeof geo.long === 'number' && typeof geo.lat === 'number' && !(geo.lat === 0 && geo.long === 0);
};

export const isItineraryAvailable = (itinerary?: ItineraryReference): boolean => {
    return (itinerary && !!itinerary.itineraryType) || false;
};

export const createBookingFromUrl = (isCartMigrationEnabled: boolean, selectedOfferOption?: EnrichedOfferOption) => {
    if (!selectedOfferOption) return '';

    const activityToken = selectedOfferOption?.offerOptionToken || '';
    const loyaltyOffer = mapLoyaltyOfferByPricing(getElementAtIndex(selectedOfferOption?.pricingSummary?.pricing, 0));
    // We support BE to return only 1 pricing and 1 offer at availability endpoint level mapLoyaltyOfferByPricing
    const activityOfferToken = loyaltyOffer.loyaltyToken || '';
    const bookingFormUrl = getBookingFormUrl(activityToken, activityOfferToken, isCartMigrationEnabled);
    return bookingFormUrl;
};

export const getOverlayType = (isCartInterstitialEnabled: boolean) => {
    if (isCartInterstitialEnabled) {
        return OverlayType.CartInterstitial;
    }
    return OverlayType.CartSummary;
};

export const generateDateChips = (index: number, size: number, schedule: AvailabilitySchedule[]): AvailabilitySchedule[] => {
    const dateChipTemp = schedule.slice(index, size + index);
    // Some part of date chips is not in the availability schedule
    // fill in the rest with isAvailable = true until the size is reached
    while (dateChipTemp.length < size) {
        const dateTemp = parseISO(dateChipTemp[dateChipTemp.length - 1].date || '');
        const dateTempPlusOne = addDays(dateTemp, 1);
        dateChipTemp.push({
            date: format(dateTempPlusOne, 'yyyy-MM-dd'),
            isAvailable: true,
        });
    }
    return dateChipTemp;
};

export const dateChipGenerator = (
    dateChipsSize: number,
    selectedDate: Date,
    dateChips: AvailabilitySchedule[],
    schedule: AvailabilitySchedule[],
    isAllUnavailable = false,
): AvailabilitySchedule[] => {
    const selectedDateString = format(selectedDate, 'yyyy-MM-dd');
    const initialDateChips = schedule.length > dateChipsSize ? schedule.slice(0, dateChipsSize) : schedule;
    const index = schedule.findIndex((x) => x.date === selectedDateString);
    const dateChipSchedule = dateChips.length > 0 ? dateChips : initialDateChips;
    if (index !== -1) {
        // Selected date is in the availability schedule
        const selectedDateInRange = dateChipSchedule.some((x) => x.date === selectedDateString);

        if (!selectedDateInRange) {
            return generateDateChips(index, dateChipsSize, schedule);
        }
        return dateChipSchedule;
    }
    // Selected date is not in the availability schedule,  generate schedule with isAvailable = true
    const selectedDateInRange = dateChips.some((x) => x.date === selectedDateString);
    if (!selectedDateInRange) {
        const dateChipsTemp = Array.from({ length: dateChipsSize }).map((_, i) => {
            const date = addDays(selectedDate, i);
            return {
                date: format(date, 'yyyy-MM-dd'),
                isAvailable: !isAllUnavailable,
                status: ActivityAvailabilityStatus.AVAILABLE,
            };
        });
        return dateChipsTemp;
    }
    return dateChips;
};

const formatPriceNoDecimal = (price: number, locale: string): string => {
    return PriceFormatter.format(price, locale, { decimalPlaces: 0 });
};

const formatPRiceWith2Decimal = (price: number, locale: string): string => {
    return PriceFormatter.format(price, locale, { decimalPlaces: 2 });
};

export const formatPriceWithAbbreviation = (price: number, locale: string): string => {
    if (price >= 1000000) {
        const priceLabel = formatPriceNoDecimal(price / 1000000, locale);
        return `${priceLabel}M`;
    }

    if (price >= 10000) {
        const priceLabel = formatPriceNoDecimal(price / 1000, locale);
        return `${priceLabel}K`;
    }

    if (price < 100) {
        return formatPRiceWith2Decimal(price, locale);
    }

    return formatPriceNoDecimal(price, locale);
};

export const getOfferGroupDetailByRef = (detail: Detail, offerGroupDetailRef: string | undefined): OfferGroupDetail | null => {
    if (!offerGroupDetailRef) return null;
    const offerGroupDetail = detail.offerGroupDetails?.filter((d) => d.offerGroupDetailsReference === offerGroupDetailRef);
    return offerGroupDetail && offerGroupDetail.length > 0 ? offerGroupDetail[0] : null;
};

export const getAppliedPromocodeItem = (pricing: Pricing, experiments: Experiments): DisplayPromocodeItem => {
    if (isB(ExperimentId.ACTB_AUTO_APPLIED_PRICE_PEEK, experiments)) {
        return pricing?.display?.perBook?.promocodeItems?.filter((item) => item.isApplied)[0] || {};
    }
    return {};
};
